import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Writes the scroll-up presentation directly to the sticky wrapper.
   * Safari can otherwise repaint the wrapper after a bounce at scrollTop 0
   * without repainting the header contents selected through :has().
   * @param {string | null} stickyMode
   * @param {string | null} stickyState
   */
  #syncStickyPresentation = (stickyMode, stickyState) => {
    if (stickyMode !== 'scroll-up') return;

    this.#hideOffset = stickyState === 'idle' ? Infinity : 0;
    this.#applyHideOffset(true);
  };

  /**
   * How far the header has slid up behind the announcement bar, in px.
   * It follows the scroll distance while scrolling down so the header leaves
   * at the pace of the page instead of vanishing on the first pixel.
   */
  #hideOffset = 0;

  /** Consecutive upward scroll distance in px, reset whenever scrolling down. */
  #upTravel = 0;

  /**
   * Distance needed to move the header fully out of view, behind the announcement bar.
   * @param {HTMLElement} headerSection
   * @returns {number}
   */
  #getHideDistance = (headerSection) => {
    const announcementOffset = parseFloat(getComputedStyle(headerSection).getPropertyValue('--header-announcement-offset'));
    return headerSection.offsetHeight + Math.max(0, Number.isFinite(announcementOffset) ? announcementOffset : 0);
  };

  /**
   * Writes the current hide offset to the sticky wrapper.
   * @param {boolean} animate - Animate the move (reveal/snap) or follow the scroll directly
   */
  #applyHideOffset = (animate) => {
    const headerSection = this.closest('.header-section');
    if (!headerSection) return;

    const distance = this.#getHideDistance(headerSection);
    const hidden = this.#hideOffset >= distance - 1;
    if (hidden) this.#hideOffset = Infinity;
    else this.#hideOffset = Math.max(0, this.#hideOffset);

    this.style.setProperty('opacity', '1');
    headerSection.style.transition = animate ? '' : 'none';
    headerSection.style.transform = hidden
      ? 'translate3d(0, calc(-100% - var(--header-announcement-offset, 0px)), 0)'
      : `translate3d(0, ${-this.#hideOffset}px, 0)`;
    headerSection.style.pointerEvents = hidden ? 'none' : 'auto';
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (!alwaysSticky || this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
      if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode !== 'always' && stickyMode !== 'scroll-up') return;

    const scrollTop = getScrollTop();
    const delta = scrollTop - this.#lastScrollTop;
    // Slow scrolling and touch jitter report tiny reversals. Only a deliberate
    // upward move (a few px in a row) counts as scrolling up, otherwise the
    // header flips between revealed and hidden and appears to shake.
    this.#upTravel = delta < 0 ? this.#upTravel - delta : 0;
    const isScrollingUp = stickyMode === 'scroll-up' ? this.#upTravel >= 10 : delta < 0;
    // The header may be stacked below a visible sticky announcement bar, so its
    // visual top is not a reliable indicator of the scroll container's top.
    const isAtTop = scrollTop <= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isAtTop) {
      // The top position is always an explicitly visible header state. This
      // also covers Safari's final overscroll/bounce frame, where the scroll
      // direction can be reported as unchanged.
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'none';
    } else if (isScrollingUp) {
      // show sticky header when scrolling up
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else {
      this.dataset.scrollDirection = 'down';
    }

    const headerSection = this.closest('.header-section');
    if (isAtTop || isScrollingUp || !headerSection) {
      this.#hideOffset = 0;
      this.#applyHideOffset(true);
    } else {
      // Scrolling down: slide the header away at the speed of the page.
      const distance = this.#getHideDistance(headerSection);
      const current = Number.isFinite(this.#hideOffset) ? this.#hideOffset : distance;
      this.#hideOffset = Math.max(0, Math.min(distance, current + delta));
      this.#applyHideOffset(false);
      this.dataset.stickyState = this.#hideOffset === Infinity ? 'idle' : 'active';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#lastScrollTop = getScrollTop();

      if (stickyMode === 'scroll-up') {
        this.dataset.stickyState = this.#lastScrollTop > 0 ? 'idle' : 'active';
        this.dataset.scrollDirection = this.#lastScrollTop > 0 ? 'down' : 'none';
      }

      this.#syncStickyPresentation(stickyMode, this.dataset.stickyState);

      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    const headerSection = this.closest('.header-section');
    if (headerSection) {
      headerSection.style.removeProperty('transform');
      headerSection.style.removeProperty('transition');
      headerSection.style.removeProperty('pointer-events');
    }
    this.style.removeProperty('opacity');
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
